import { type RefObject, useEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';
import debounce from 'lodash/debounce';

import { functionWithFG } from '@atlaskit/platform-feature-flags-react';

import { type ScrollDirection } from './types';

const DEBOUNCE_DURATION = 50;
const MIN_SCROLL_OFFSET = 0;

interface UseScrollDirectionOptions {
	onScroll?: (direction: ScrollDirection) => void;
}

export const useScrollDirection: typeof useScrollDirectionNEW = functionWithFG(
	'navx-4866-migrate-messages-from-nav-error-boundary',
	useScrollDirectionNEW,
	useScrollDirectionOLD,
);

function useScrollDirectionNEW(
	ref: RefObject<HTMLDivElement>,
	options?: UseScrollDirectionOptions,
): void {
	const lastScrollY = useRef(0);
	const onScrollCallback = options?.onScroll;

	useEffect(() => {
		if (!ref || !ref.current) {
			return;
		}

		const onScroll = debounce(() => {
			const scrollY = ref.current!.scrollTop;
			const direction: ScrollDirection = scrollY > lastScrollY.current ? 'down' : 'up';

			if (
				scrollY - lastScrollY.current > MIN_SCROLL_OFFSET ||
				scrollY - lastScrollY.current < -1 * MIN_SCROLL_OFFSET
			) {
				// Call the callback directly in the event handler to avoid a chain-of-computation
				// anti-pattern (effect → state → effect).
				if (onScrollCallback) {
					onScrollCallback(direction);
				}
			}

			lastScrollY.current = scrollY > 0 ? scrollY : 0;
		}, DEBOUNCE_DURATION);

		return bind(ref.current, {
			type: 'scroll',
			listener: onScroll,
			options: {
				capture: true,
			},
		});
		// Only depend on ref and onScrollCallback — no need to re-subscribe when scrollDirection changes.
	}, [ref, onScrollCallback]);
}

function useScrollDirectionOLD(
	ref: RefObject<HTMLDivElement>,
	options?: UseScrollDirectionOptions,
): ScrollDirection | undefined {
	const lastScrollY = useRef(0);
	const [scrollDirection, setScrollDirection] = useState<ScrollDirection | undefined>(undefined);
	const onScrollCallback = options?.onScroll;

	useEffect(() => {
		if (!ref || !ref.current) {
			return;
		}

		const onScroll = debounce(() => {
			const scrollY = ref.current!.scrollTop;
			const direction: ScrollDirection = scrollY > lastScrollY.current ? 'down' : 'up';

			if (
				scrollY - lastScrollY.current > MIN_SCROLL_OFFSET ||
				scrollY - lastScrollY.current < -1 * MIN_SCROLL_OFFSET
			) {
				// Always call setScrollDirection — React will bail out if the value is unchanged.
				setScrollDirection(direction);
				// Call the callback directly in the event handler to avoid a chain-of-computation
				// anti-pattern (effect → state → effect).
				if (onScrollCallback) {
					onScrollCallback(direction);
				}
			}

			lastScrollY.current = scrollY > 0 ? scrollY : 0;
		}, DEBOUNCE_DURATION);

		return bind(ref.current, {
			type: 'scroll',
			listener: onScroll,
			options: {
				capture: true,
			},
		});
		// Only depend on ref and onScrollCallback — no need to re-subscribe when scrollDirection changes.
	}, [ref, onScrollCallback]);

	return scrollDirection;
}
